import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  DollarSign,
  AlertTriangle,
  Users,
  TrendingUp,
  ArrowUpRight,
  Briefcase,
  ClipboardList,
  ChevronRight,
  Plus,
  Lock,
  ShieldCheck,
  ShieldAlert,
  Receipt,
  RefreshCw,
  Bell,
  Download,
} from '@/lib/icons';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { formatCurrency, truncateId } from '@/lib/utils';
import { canAccessRoute, getLandingPath } from '@/lib/auth/route-access';
import { isSuperAdmin } from '@/lib/auth/roles';
import { hasPermission } from '@/lib/auth/permissions';
import {
  fetchDashboardStats,
  fetchRecentPayments,
  fetchOverdueContracts,
  clearError,
} from '@/features/dashboard/dashboard-slice';
import {
  DEFAULT_DASHBOARD_PERIOD,
  DashboardPeriodSchema,
  type DashboardPeriod,
  type RecentPaymentModel,
} from '@/features/dashboard/dashboard';
import { useDashboardActivity } from '@/features/dashboard/use-dashboard-activity';
import { RepaymentsCard } from '@/features/dashboard/components/RepaymentsCard';
import { PendingKycCard } from '@/features/dashboard/components/PendingKycCard';
import { PaymentReceiptSheet } from '@/features/payments/payment-receipt-sheet';
import { getQueueStats, getLockSchedule } from '@/features/lock-engine/api';
import { getDeviceUnitStatusCounts } from '@/features/inventory/api';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Segmented } from '@/components/ui/segmented';
import { StatCard } from '@/components/dashboard/StatCard';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyButton } from '@/components/ui/copyable-id';
import { cn } from '@/lib/utils';
import { ExportDialog } from '@/features/export/components/ExportDialog';
import {
  BUSINESS_TIME_ZONE_ABBR,
  formatDateTime,
  formatDateTimeWithZone,
  formatTime,
} from '@/lib/dates';

const PERIODS = DashboardPeriodSchema.options;

// The period lives in the URL so a reload or shared link keeps it; an unknown
// value falls back to the default instead of erroring the route.
const dashboardSearchSchema = z.object({
  period: DashboardPeriodSchema.optional().catch(undefined),
});

/** Inline error state for a single dashboard section (does not blank the page). */
function SectionError({ message }: Readonly<{ message: string }>) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 py-8 text-center">
      <AlertTriangle className="h-5 w-5 text-amber-500" />
      <span className="text-sm font-medium text-muted-foreground">
        Couldn’t load this section
      </span>
      <span className="text-xs text-muted-foreground/70">{message}</span>
    </div>
  );
}

/** Stack of shimmer rows shown while a section is loading. */
function SectionSkeleton({ rows = 5 }: Readonly<{ rows?: number }>) {
  return (
    <div className="space-y-2 py-1">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full rounded-md" />
      ))}
    </div>
  );
}

export const Route = createFileRoute('/_layout/')({
  component: DashboardPage,
  validateSearch: dashboardSearchSchema,
});

/* ── Quick Action Link ───────────────────────────────────── */

function QuickAction({
  icon,
  label,
  description,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  to: string;
}) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate({ to })}
      className="group flex items-start gap-3 rounded-xl bg-surface-container-low p-4 text-left transition-all duration-200 hover:shadow-md hover:bg-surface-container-lowest card-shine"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <span className="font-semibold text-sm text-foreground">{label}</span>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
          {description}
        </p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground mt-1 group-hover:translate-x-0.5 transition-transform" />
    </button>
  );
}

/* ── Dashboard ────────────────────────────────────────────── */

/* ── Devices Status Pie Colors ────────────────────────────────── */
// Mazeed Design System semantic palette — keeps the dashboard pie chart in
// lockstep with badges, status pills, and toasts.
const DEVICE_STATUS_COLORS: Record<string, string> = {
  procured: '#8551C9', // pending
  in_stock: '#1FA463', // success
  reserved: '#E8A317', // warning
  financed: '#2D7BF4', // info
  returned: '#9D6FD7', // pending-400
  damaged: '#E5354B', // error
  written_off: '#8794B5', // ink-400
};

/* ── Operations Shortcuts ─────────────────────────────────────── */
// Entry points into the day-to-day operating surfaces. Rendered only for the
// routes the signed-in role can actually land on, so the card never shows a
// tile that would bounce the user at the guard.
const OPERATIONS_SHORTCUTS = [
  {
    key: 'autoDeductions',
    path: '/settings/operations',
    icon: <RefreshCw className="h-4 w-4" />,
  },
  {
    key: 'cashCollections',
    path: '/settings/operations',
    icon: <Receipt className="h-4 w-4" />,
  },
  { key: 'deviceLock', path: '/lock-engine', icon: <Lock className="h-4 w-4" /> },
  {
    key: 'auditLogs',
    path: '/audit-logs',
    icon: <ShieldCheck className="h-4 w-4" />,
  },
  { key: 'rbac', path: '/users', icon: <Users className="h-4 w-4" /> },
  {
    key: 'notifications',
    path: '/notifications',
    icon: <Bell className="h-4 w-4" />,
  },
  {
    key: 'exceptions',
    path: '/collections',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
];

function DashboardPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    if (user && !canAccessRoute(user, '/')) {
      const landing = getLandingPath(user);
      if (landing !== '/') navigate({ to: landing });
    }
  }, [user, navigate]);
  const {
    stats,
    recentPayments,
    overdueContracts,
    isLoading,
    error: dashboardError,
    recentPaymentsLoading,
    recentPaymentsError,
    overdueLoading,
    overdueError,
  } = useAppSelector((state) => state.dashboard);
  const period = Route.useSearch().period ?? DEFAULT_DASHBOARD_PERIOD;
  const setPeriod = (next: DashboardPeriod) =>
    navigate({
      to: '/',
      search: {
        period: next === DEFAULT_DASHBOARD_PERIOD ? undefined : next,
      },
      replace: true,
    });
  const [lastUpdated] = useState(() => new Date());
  // Recent-transaction receipt drawer. Holds the row alongside the id so the
  // sheet can render the customer/contract instantly while its detail request
  // is still in flight.
  const [receiptPayment, setReceiptPayment] =
    useState<RecentPaymentModel | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';
  const selectedTenantId = useAppSelector(
    (state) => state.tenant.selectedTenantId
  );
  // Bypass users (e.g. SuperAdmin) have no tenant on their JWT; tenant-scoped
  // endpoints reject them with 400 until they pick a tenant via TenantSelector.
  const hasTenantContext =
    !!user && (!isSuperAdmin(user) || !!selectedTenantId);

  // The lock engine has its own administrative scope; only a holder gets the
  // status bar and its polling.
  const canReadLockEngine = hasPermission(user, 'lock_engine', 'read');

  // Per-widget permission gates mirror the backend scopes:
  //   dashboard widgets  → dashboard:read   (route already requires it)
  //   pending-KYC card   → applications:read
  //   devices pie chart  → device_units:read
  // Widgets the user can't read are hidden so we don't fire endpoints that
  // would just 403.
  const canReadDashboard = hasPermission(user, 'dashboard', 'read');
  const canReadApplications = hasPermission(user, 'applications', 'read');
  const canReadDeviceUnits = hasPermission(user, 'device_units', 'read');
  const canReadPayments = hasPermission(user, 'payments', 'read');
  const canReadContracts = hasPermission(user, 'contracts', 'read');
  const canCreateApplications = hasPermission(user, 'applications', 'create');
  // Quick actions and shortcut tiles are links; the route guard is the
  // authority on where this user may land.
  const canGo = (path: string) => canAccessRoute(user, path);
  const visibleShortcuts = useMemo(
    () => OPERATIONS_SHORTCUTS.filter((s) => canAccessRoute(user, s.path)),
    [user]
  );

  // Lock engine data
  const { data: queueStatsData } = useQuery({
    queryKey: ['lock-engine', 'queue-stats'],
    queryFn: getQueueStats,
    refetchInterval: 60_000,
    enabled: hasTenantContext && canReadLockEngine,
  });
  const { data: scheduleData } = useQuery({
    queryKey: ['lock-engine', 'schedule'],
    queryFn: getLockSchedule,
    refetchInterval: 60_000,
    enabled: canReadLockEngine,
  });

  // Device status counts for the pie chart. Backed by /device-units/status-counts
  // (aggregate query) so we don't pull the full inventory list just to count.
  const { data: deviceCountsData } = useQuery({
    queryKey: ['device-units-status-counts'],
    queryFn: getDeviceUnitStatusCounts,
    staleTime: 60_000,
    enabled: hasTenantContext && canReadDeviceUnits,
  });

  const queueStats = queueStatsData?.value;
  const lockSchedule = scheduleData?.value;
  const deviceStatusCounts = deviceCountsData?.value ?? {};

  const devicePieData = Object.entries(deviceStatusCounts)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' '),
      value: count,
      color: DEVICE_STATUS_COLORS[status] || '#6b7280',
    }));

  useEffect(() => {
    if (!hasTenantContext || !canReadDashboard) return;
    dispatch(fetchDashboardStats());
    dispatch(fetchRecentPayments());
    dispatch(fetchOverdueContracts());
  }, [dispatch, hasTenantContext, canReadDashboard]);

  const activityQuery = useDashboardActivity(period, {
    tenantId: selectedTenantId ?? null,
    enabled: hasTenantContext && canReadDashboard,
  });
  const activity = activityQuery.data;
  const activityLoading = activityQuery.isPending && activityQuery.isFetching;

  const lastUpdatedLabel = useMemo(() => {
    const seconds = Math.max(
      1,
      Math.floor((Date.now() - lastUpdated.getTime()) / 1000)
    );
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${formatTime(lastUpdated)} ${BUSINESS_TIME_ZONE_ABBR}`;
  }, [lastUpdated]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Operations Header ───────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-heading-md tracking-tight text-foreground">
            {t('dashboard.operations')}
          </h1>
          <p className="mt-1 text-body-sm text-muted-foreground">
            {t('dashboard.liveSnapshot', { time: lastUpdatedLabel })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canReadDashboard && (
            <Segmented<DashboardPeriod>
              ariaLabel={t('dashboard.periodAria')}
              value={period}
              onChange={setPeriod}
              items={PERIODS.map((p) => ({
                value: p,
                label: t(`dashboard.period.${p}`),
              }))}
            />
          )}
          {canReadDashboard && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportOpen(true)}
              className="h-9 gap-1.5"
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          )}
          {canCreateApplications && (
            <Button
              size="sm"
              onClick={() => navigate({ to: '/applications/new' })}
              className="h-9"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              {t('dashboard.newApplication')}
            </Button>
          )}
        </div>
      </div>
      {/* ── Error Banner ─────────────────────────────── */}
      {dashboardError && (
        <div className="rounded-xl bg-red-50/80 dark:bg-red-950/20 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-red-500/15">
                <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="font-medium text-sm text-red-800 dark:text-red-300">
                  {dashboardError.includes('timeout')
                    ? 'Backend service is not responding'
                    : 'Dashboard data unavailable'}
                </p>
                <p className="text-xs text-red-600/80 dark:text-red-400/80 mt-0.5">
                  {dashboardError.includes('timeout')
                    ? 'Ensure the offerings-service service is running on port 8092'
                    : dashboardError}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-red-300/50 text-red-700 hover:bg-red-100 dark:border-red-700/50 dark:text-red-300 dark:hover:bg-red-900/30 shrink-0 ml-4 rounded-xl"
              onClick={() => {
                dispatch(clearError());
                dispatch(fetchDashboardStats());
                dispatch(fetchRecentPayments());
                dispatch(fetchOverdueContracts());
                void activityQuery.refetch();
              }}
            >
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* ── KPI Strip (4 cards · design's Operations layout) ─── */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 animate-stagger-in">
        {canReadContracts && (
          <StatCard
            title={t('dashboard.stats.activePlans')}
            value={stats?.activeContracts ?? 0}
            icon={<FileText className="h-4 w-4" />}
            tone="brand"
            loading={isLoading}
            helperText={
              activity &&
              t(`dashboard.newPlans.${period}`, {
                count: activity.newContracts,
              })
            }
            onClick={() => navigate({ to: '/contracts' })}
            delay={0}
          />
        )}
        {canReadContracts && (
          <StatCard
            title={t('dashboard.stats.outstanding')}
            value={formatCurrency(stats?.outstandingBalance ?? 0, currency)}
            icon={<DollarSign className="h-4 w-4" />}
            tone="info"
            loading={isLoading}
            onClick={() => navigate({ to: '/portfolio' })}
            delay={50}
          />
        )}
        {canReadContracts && (
          <StatCard
            title={t('dashboard.stats.collectionRate')}
            value={
              activity?.collectionRate != null
                ? `${activity.collectionRate.toFixed(1)}%`
                : '—'
            }
            icon={<TrendingUp className="h-4 w-4" />}
            tone="success"
            loading={activityLoading}
            helperText={
              activity &&
              (activity.expected > 0
                ? t('dashboard.collectionRateHelper', {
                    collected: formatCurrency(
                      activity.installmentCollected,
                      currency
                    ),
                    due: formatCurrency(activity.expected, currency),
                  })
                : t('dashboard.nothingDue'))
            }
            className={cn(
              'transition-opacity duration-200',
              activityQuery.isPlaceholderData && 'opacity-60'
            )}
            onClick={() => navigate({ to: '/portfolio' })}
            delay={100}
          />
        )}
        {canReadPayments && (
          <StatCard
            title={t('dashboard.stats.atRisk')}
            value={stats?.overduePaymentsCount ?? 0}
            icon={<AlertTriangle className="h-4 w-4" />}
            tone="warning"
            loading={isLoading}
            onClick={() =>
              navigate({ to: canGo('/collections') ? '/collections' : '/payments' })
            }
            delay={150}
          />
        )}
      </div>

      {/* ── Repayments + Pending KYC (design's 2-col Operations row) ─── */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Repayments chart · 2fr */}
        <RepaymentsCard
          period={period}
          activity={activity}
          isLoading={activityLoading}
          isStale={activityQuery.isPlaceholderData}
          error={activityQuery.error?.message ?? null}
          currency={currency}
          onRetry={() => void activityQuery.refetch()}
        />

        {/* Pending KYC · 1fr — only when the role can read applications */}
        {canReadApplications && (
          <PendingKycCard
            tenantId={selectedTenantId ?? null}
            enabled={hasTenantContext}
            currency={currency}
          />
        )}
      </div>

      {/* ── Recent Transactions ─────────────────────────── */}
      {canReadPayments && (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">
                {t('dashboard.recentTransactions')}
              </CardTitle>
              <CardDescription className="text-xs">
                {t('dashboard.recentTransactionsDesc')}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs hover:text-primary"
              onClick={() => navigate({ to: '/payments' })}
            >
              {t('common.viewAll')} <ArrowUpRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {recentPaymentsLoading ? (
            <SectionSkeleton rows={6} />
          ) : recentPaymentsError ? (
            <SectionError message={recentPaymentsError} />
          ) : recentPayments.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('payments.columns.contract')}</TableHead>
                  <TableHead>{t('payments.columns.customer')}</TableHead>
                  <TableHead>{t('payments.columns.method')}</TableHead>
                  <TableHead className="text-right">
                    {t('payments.columns.amount')}
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    {t('payments.columns.date')}
                  </TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">
                      {t('payments.receipt.title')}
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentPayments.slice(0, 10).map((payment) => (
                  <TableRow
                    key={payment.id}
                    // `contractNumber` carries the contract's UUID — contracts
                    // have no human-readable number in the schema, and the
                    // dashboard endpoint stringifies `contract.id` into this
                    // field — so it doubles as the route param.
                    //
                    // Mouse convenience only — the contract link in the first
                    // cell is the real keyboard/assistive-tech affordance, so
                    // the row itself stays a plain (non-focusable) row and
                    // table semantics are preserved.
                    onClick={(e) => {
                      // Let the nested link / copy / receipt controls win.
                      if ((e.target as HTMLElement).closest('a,button')) return;
                      navigate({
                        to: '/contracts/$contractId',
                        params: { contractId: payment.contractNumber },
                        search: { highlight: payment.id },
                      });
                    }}
                    className="group cursor-pointer hover:bg-accent/30"
                  >
                    <TableCell className="font-medium">
                      {/* Link and copy button are siblings, not nested — an
                          <a> may not contain a <button>. */}
                      <span className="group/id inline-flex items-center gap-1.5">
                        <Link
                          to="/contracts/$contractId"
                          params={{ contractId: payment.contractNumber }}
                          search={{ highlight: payment.id }}
                          title={payment.contractNumber}
                          className="rounded-sm font-mono tracking-tight text-foreground transition-colors hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background group-hover:text-primary"
                        >
                          {truncateId(payment.contractNumber)}
                        </Link>
                        <CopyButton
                          value={payment.contractNumber}
                          label={t('payments.receipt.copyContractId')}
                          copiedLabel={t('common.copied')}
                        />
                      </span>
                    </TableCell>
                    <TableCell>{payment.customerName}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {t(`payments.method.${payment.paymentMethod}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-bold text-primary">
                      {formatCurrency(payment.amount, currency)}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden sm:table-cell text-xs">
                      <span title={formatDateTimeWithZone(payment.createdAt)}>
                        {formatDateTime(payment.createdAt)}
                      </span>
                    </TableCell>
                    <TableCell className="w-10 p-0 pr-3 text-right">
                      <button
                        type="button"
                        onClick={() => setReceiptPayment(payment)}
                        aria-label={t('payments.receipt.openFor', {
                          customer: payment.customerName,
                          amount: formatCurrency(payment.amount, currency),
                        })}
                        title={t('payments.receipt.title')}
                        // Always visible on touch-sized viewports, where a
                        // hover-reveal would leave it unreachable.
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-all duration-150 hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background md:opacity-0 md:group-hover:opacity-100"
                      >
                        <Receipt className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 gap-2 text-muted-foreground">
              <span className="text-sm">{t('dashboard.noRecentPayments')}</span>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Receipt drawer for the recent-transactions rows. Kept mounted so the
          close animation can play out; the detail query is gated on `open`. */}
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />
      <PaymentReceiptSheet
        paymentId={receiptPayment?.id ?? null}
        open={!!receiptPayment}
        onOpenChange={(open) => {
          if (!open) setReceiptPayment(null);
        }}
        fallbackCustomerName={receiptPayment?.customerName}
        fallbackContractId={receiptPayment?.contractNumber}
      />

      {/* ── Lock Engine Status Bar (admin-only) ───────── */}
      {canReadLockEngine && (
        <Card
          className={cn(
            'relative overflow-hidden',
            lockSchedule?.canLockNow ? 'bg-emerald-500/5' : 'bg-red-500/5'
          )}
        >
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {lockSchedule?.canLockNow ? (
                <div className="p-2 rounded-lg bg-emerald-500/15">
                  <ShieldCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
              ) : (
                <div className="p-2 rounded-lg bg-red-500/15">
                  <ShieldAlert className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
              )}
              <div>
                <p className="text-sm font-medium">
                  {t('dashboard.lockEngine')}:{' '}
                  {lockSchedule?.canLockNow
                    ? t('common.active')
                    : t('dashboard.restricted')}
                  {lockSchedule?.blackoutReason && (
                    <span className="text-muted-foreground font-normal">
                      {' '}
                      — {lockSchedule.blackoutReason}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {queueStats
                    ? t('dashboard.queuePending', { count: queueStats.pending })
                    : t('dashboard.queueLoading')}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: '/lock-engine' })}
              className="shrink-0"
            >
              <Lock className="h-3.5 w-3.5 mr-1.5" />
              {t('dashboard.viewLockEngine')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Quick Actions ───────────────────────────────── */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 animate-stagger-in">
        {[
          {
            icon: <ClipboardList className="h-5 w-5" />,
            label: t('dashboard.quickActions.processApplications'),
            description: t('dashboard.quickActions.processApplicationsDesc'),
            to: '/applications',
          },
          {
            icon: <DollarSign className="h-5 w-5" />,
            label: t('dashboard.quickActions.recordPayment'),
            description: t('dashboard.quickActions.recordPaymentDesc'),
            to: '/payments/record',
          },
          {
            icon: <Briefcase className="h-5 w-5" />,
            label: t('dashboard.quickActions.portfolio'),
            description: t('dashboard.quickActions.portfolioDesc'),
            to: '/portfolio',
          },
          {
            icon: <Lock className="h-5 w-5" />,
            label: t('dashboard.quickActions.lockEngine'),
            description: t('dashboard.quickActions.lockEngineDesc'),
            to: '/lock-engine',
          },
          {
            icon: <AlertTriangle className="h-5 w-5" />,
            label: t('dashboard.quickActions.collections'),
            description: t('dashboard.quickActions.collectionsDesc'),
            to: '/collections',
          },
          {
            icon: <Users className="h-5 w-5" />,
            label: t('nav.customers'),
            description: t('dashboard.quickActions.customersDesc'),
            to: '/customers',
          },
        ]
          .filter((a) => canGo(a.to))
          .map((a) => (
            <QuickAction
              key={a.to}
              icon={a.icon}
              label={a.label}
              description={a.description}
              to={a.to}
            />
          ))}
      </div>

      {/* ── Devices + Overdue Contracts ────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        {canReadDeviceUnits && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {t('dashboard.devicesByStatus')}
            </CardTitle>
            <CardDescription className="text-xs">
              {t('dashboard.inventoryBreakdown')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {devicePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={devicePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={56}
                    outerRadius={92}
                    paddingAngle={4}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {devicePieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      borderColor: 'hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[260px] text-muted-foreground">
                {t('dashboard.noDevicesEnrolled')}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {canReadContracts && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  {t('dashboard.overdueContracts')}
                </CardTitle>
                <CardDescription className="text-xs">
                  {t('dashboard.accountsRequiringAttention')}
                </CardDescription>
              </div>
              {canGo('/collections') && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs hover:text-primary"
                  onClick={() => navigate({ to: '/collections' })}
                >
                  {t('dashboard.viewAll')}{' '}
                  <ArrowUpRight className="ml-1 h-3 w-3" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {overdueLoading ? (
              <SectionSkeleton rows={5} />
            ) : overdueError ? (
              <SectionError message={overdueError} />
            ) : overdueContracts.length > 0 ? (
              <div className="space-y-1">
                {overdueContracts.slice(0, 5).map((contract) => (
                  <div
                    key={contract.id}
                    className="flex items-center justify-between rounded-md p-2 -mx-2 hover:bg-surface-container-low transition-colors"
                  >
                    <div className="min-w-0">
                      <Link
                        to="/contracts/$contractId"
                        params={{ contractId: contract.id }}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {contract.contractNumber}
                      </Link>
                      <p className="text-xs text-muted-foreground truncate">
                        {contract.customerName}
                      </p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <span className="text-sm font-semibold text-destructive">
                        {formatCurrency(contract.overdueAmount, currency)}
                      </span>
                      <p className="text-[10px] text-muted-foreground">
                        {t('dashboard.dOverdue', {
                          days: contract.daysPastDue,
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                {t('dashboard.noOverdueContracts')}
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>
      {/* ── Operations Shortcuts ───────────────────────── */}
      {visibleShortcuts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {t('dashboard.operationsShortcuts')}
            </CardTitle>
            <CardDescription className="text-xs">
              {t('dashboard.operationsShortcutsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {visibleShortcuts.map((s) => (
                <Link
                  key={s.key}
                  to={s.path}
                  className="group flex items-center gap-2.5 rounded-lg bg-surface-container-low p-3 transition-colors duration-200 hover:bg-surface-container-lowest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <span className="shrink-0 text-muted-foreground transition-colors group-hover:text-primary">
                    {s.icon}
                  </span>
                  <span className="truncate text-xs font-medium">
                    {t(`dashboard.shortcuts.${s.key}`)}
                  </span>
                  <ChevronRight className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
